(ns manage.ad-variant.hooks
  (:require [cljs-bean.core :refer [->js]]
            [clojure.set :as set]
            [common.queries :as queries :refer [use-query]]
            [common.util :as util]
            [manage.ad-variant.page.columns :as columns]
            [manage.ad-variant.queries :as q]
            [manage.content-variant-page.queries :as content-queries]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-trigger-content-refetch [{:keys [advertiser-id feed-id]}]
  (let [latest-import-status-response (use-query (content-queries/latest-feed-import-status-query advertiser-id feed-id))
        import-id (get-in latest-import-status-response [:data :data :id])
        import-state (get-in latest-import-status-response [:data :data :state])
        previous-import-id (uix/use-ref nil)]

    ;; When import-status (with polling) shows that we got a new import,
    ;; trigger content refetch when the import-status is completed.
    (uix/use-effect (fn []
                      (let [prev @previous-import-id]
                        (when (and (not= prev import-id) (= "completed" import-state))
                          (queries/invalidate-queries {:queryKey ["content-build-variants" advertiser-id feed-id]})
                          (reset! previous-import-id import-id))))
                    [advertiser-id feed-id import-id import-state])
    latest-import-status-response))

(defhook use-trigger-ad-variant-refetch [{:keys [advertiser-id adset-id]}]
  (let [adset-content-build-response (use-query (q/adset-content-build-query {:advertiser-id advertiser-id :adset-id adset-id}))
        ad-variants-updated-at (get-in adset-content-build-response [:data :data :latest-ad-variants-updated-at])
        ad-variant-creation-status (get-in adset-content-build-response [:data :data :ad-variant-creation-status])
        previous-ad-variants-updated-at (uix/use-ref nil)]

    ;; When adset-content-build (with polling) shows that ad variants were updated and creation is completed,
    ;; trigger ad variants refetch.
    (uix/use-effect (fn []
                      (let [prev @previous-ad-variants-updated-at]
                        (when (and (not= prev ad-variants-updated-at)
                                   (= "completed" ad-variant-creation-status))
                          (queries/invalidate-queries {:queryKey ["ad-variants-by-content-id" advertiser-id adset-id]})
                          (reset! previous-ad-variants-updated-at ad-variants-updated-at))))
                    [advertiser-id adset-id ad-variants-updated-at ad-variant-creation-status])
    adset-content-build-response))

(defhook use-template-dimensions []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {ad-builders :data} (use-query (q/ad-builders-upgrade-hints-query {:adset-id adset-id}))]
    (->> ad-builders
         (mapcat :ad-formats)
         (keep #(-> %
                    :current-revision
                    (select-keys [:width :height :duration])
                    util/filter-nils-map
                    not-empty))
         set)))

(defhook use-ad-variant-dimensions []
  (let [url-context (use-subscribe [:url-context])
        {ad-variants :data} (use-query (q/ad-variants-by-content-id-query
                                        (assoc url-context :refetch false)
                                        nil
                                        {:pagination-model #js {:page 0 :pageSize 20}}))]
    (->> ad-variants
         :data
         (map #(-> %
                   :data
                   :dimensions
                   (select-keys [:width :height :duration])
                   util/filter-nils-map))
         set)))

(defhook use-dimensions []
  (let [template-dimensions (use-template-dimensions)
        ad-variant-dimensions (use-ad-variant-dimensions)]
    (set/union template-dimensions ad-variant-dimensions)))

(defhook use-has-multiple-templates []
  (let [template-dimensions (use-template-dimensions)]
    (< 1 (count template-dimensions))))

(defhook use-has-multiple-dimensions []
  (let [dimensions (use-dimensions)]
    (< 1 (count dimensions))))

(defhook use-ad-variant-grouping-props
  [{:keys [api-ref groups-opened-by-default disable-grouping initial-column-visibility-model]}]
  (let [has-multiple-dimensions (use-has-multiple-dimensions)

        template-count->group-model (uix/use-memo
                                     (fn []
                                       (if (or disable-grouping
                                               (not has-multiple-dimensions))
                                         #js []
                                         #js [(.-field columns/identifier-column)]))
                                     [has-multiple-dimensions disable-grouping])
        [group-model set-group-model] (uix/use-state template-count->group-model)

        group-model->visibility-model (uix/use-callback (fn [group-model]
                                                          (let [show-column? (empty? group-model)]
                                                            (->js (merge {(.-field columns/identifier-column) show-column?
                                                                          columns/ad-type-column-field show-column?}
                                                                         initial-column-visibility-model))))
                                                        [initial-column-visibility-model])
        initial-column-visibility-model (uix/use-memo (fn []
                                                        (group-model->visibility-model template-count->group-model))
                                                      [template-count->group-model group-model->visibility-model])
        [column-visibility-model set-column-visibility-model] (uix/use-state initial-column-visibility-model)

        identifier-grouping-column-props (columns/use-identifier-grouping-column-props
                                          {:api-ref api-ref
                                           :negated groups-opened-by-default})]

    (uix/use-effect (fn []
                      (set-group-model template-count->group-model)
                      (set-column-visibility-model (group-model->visibility-model template-count->group-model)))
                    [template-count->group-model group-model->visibility-model])

    (merge
     {:apiRef api-ref
      :disableRowGrouping false

      :rowGroupingModel group-model
      :onRowGroupingModelChange (fn [^js group-model]
                                  (set-group-model group-model)
                                  (set-column-visibility-model (group-model->visibility-model group-model)))

      :columnVisibilityModel column-visibility-model
      :onColumnVisibilityModelChange set-column-visibility-model

      :initialState #js {:columns #js {:columnVisibilityModel initial-column-visibility-model}
                         :rowGrouping #js {:model template-count->group-model}}}
     identifier-grouping-column-props)))
